/**
 * The checks of a document read from a photo or scan against Teslyn's records (F03, F09; decision 507;
 * docs/specs/document-reading.md §5): an MR front page against today and the aircraft register, an MR daily log page
 * against the day's flight entries, a fuel docket against its resupply and a fuel receipt against its refuel. Each
 * check has a state with a word, as the QC states; a mismatch is a fail and is never softened.
 */
import { chronological, hoursFlown, mrStatus, aircraftFuelType, DEFAULT_MR_WARN_H } from './aircraft.js';
import type { Aircraft, DailyEntry, FuelLedgerEntry, Uuid } from './model.js';
import {
  DOCUMENT_KIND_LABEL,
  type DocketValues,
  type DocumentKind,
  type DocumentValues,
  type MrFrontValues,
  type MrLogValues,
} from './document-reading.js';

export type DocumentCheckState = 'pass' | 'warn' | 'fail' | 'info';

export interface DocumentCheck {
  readonly key: string;
  readonly state: DocumentCheckState;
  /** One or two words beside the state's colour ("Valid", "Does not match"). */
  readonly word: string;
  readonly message: string;
}

/** MR pages warn this many days before their expiry date (question 3 of the spec; 30 days until MB says). */
export const MR_EXPIRY_WARN_DAYS = 30;
/** Litres within this of each other match (whole litres on the entries, tenths on some dockets). */
export const LITRES_TOLERANCE = 1;
/** Litres in a US gallon. */
export const LITRES_PER_USG = 3.785411784;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `25 Sep 2027` (with the year), `23 Oct` without. */
export function dayText(iso: string, withYear = true): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}${withYear ? ` ${y}` : ''}`;
}
const tenth = (n: number): number => Math.round(n * 10) / 10;
const h1 = (n: number): string => tenth(n).toFixed(1);
const litresText = (n: number): string =>
  n.toLocaleString('en-AU', { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });

function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

const STATE_ORDER: Readonly<Record<DocumentCheckState, number>> = { fail: 3, warn: 2, pass: 1, info: 0 };
/** The worst state of a set of checks ('info' when there are none or all are notes). */
export function worstState(checks: readonly { readonly state: DocumentCheckState }[]): DocumentCheckState {
  return checks.reduce<DocumentCheckState>(
    (w, c) => (STATE_ORDER[c.state] > STATE_ORDER[w] ? c.state : w),
    'info',
  );
}

// ------------------------------------------------------------------------------------------------ flights per day

export interface DayFlights {
  readonly date: string;
  /** Hours flown (survey + comp box + cals + ferry) of the aircraft's flight entries that day. */
  readonly total_h: number;
  readonly flights: readonly {
    readonly entry_id: Uuid;
    readonly flt_num: number | null;
    readonly hours: number;
  }[];
  /** Teslyn's progressive TTIS after the day's last entry, or null without the aircraft's TTIS at start. */
  readonly ttis_after_h: number | null;
}

const isFlight = (e: DailyEntry): boolean => e.entry_type === 'flight' && hoursFlown(e) > 0;

/** The aircraft's flying per day, from its live daily entries, in date order. */
export function flightsByDay(a: Aircraft, entries: readonly DailyEntry[]): DayFlights[] {
  const mine = chronological(entries.filter((e) => e.deleted_at === null && e.aircraft_id === a.id));
  const out = new Map<
    string,
    { total: number; flights: DayFlights['flights'][number][]; ttis: number | null }
  >();
  let ttis = a.ttis_at_start_h;
  for (const e of mine) {
    const h = hoursFlown(e);
    if (ttis !== null) ttis += h;
    if (!isFlight(e)) continue;
    const day = out.get(e.date) ?? { total: 0, flights: [], ttis: null };
    day.total += h;
    day.flights.push({ entry_id: e.id, flt_num: e.flt_num, hours: h });
    day.ttis = ttis;
    out.set(e.date, day);
  }
  return [...out.entries()].map(([date, d]) => ({
    date,
    total_h: tenth(d.total),
    flights: d.flights,
    ttis_after_h: d.ttis === null ? null : tenth(d.ttis),
  }));
}

/** "5.0 + 5.1" */
const sumText = (d: DayFlights): string => d.flights.map((f) => h1(f.hours)).join(' + ');

// ------------------------------------------------------------------------------------------------ MR front page

export interface MrFrontContext {
  readonly aircraft: Aircraft | null;
  readonly entries: readonly DailyEntry[];
  /** Today in the project's time, `YYYY-MM-DD`. */
  readonly today: string;
  /** The project's MR flag in hours (`mrWarnHours(project)`; 0 = Ignore). */
  readonly warnH?: number;
  readonly warnDays?: number;
}

/** The MR front page's checks: expiry date, expiry hours, and agreement with the aircraft register. */
export function mrFrontChecks(v: MrFrontValues, ctx: MrFrontContext): DocumentCheck[] {
  const out: DocumentCheck[] = [];
  const warnDays = ctx.warnDays ?? MR_EXPIRY_WARN_DAYS;
  const warnH = ctx.warnH ?? DEFAULT_MR_WARN_H;
  if (v.expiry_date === null) {
    out.push({
      key: 'expiry_date',
      state: 'warn',
      word: 'Not entered',
      message: 'Enter the expiry date from the page.',
    });
  } else {
    const left = daysBetween(ctx.today, v.expiry_date);
    out.push(
      left < 0
        ? {
            key: 'expiry_date',
            state: 'fail',
            word: 'Expired',
            message: `MR expired on ${dayText(v.expiry_date)}.`,
          }
        : left <= warnDays
          ? {
              key: 'expiry_date',
              state: 'warn',
              word: 'Expires soon',
              message:
                left === 0
                  ? `MR expires today, ${dayText(v.expiry_date)}.`
                  : `MR expires in ${left} day${left === 1 ? '' : 's'}, on ${dayText(v.expiry_date)}.`,
            }
          : {
              key: 'expiry_date',
              state: 'pass',
              word: 'Valid',
              message: `Valid to ${dayText(v.expiry_date)}.`,
            },
    );
  }
  const a = ctx.aircraft;
  const status = a ? mrStatus(a, ctx.entries, warnH) : null;
  if (v.expiry_hours === null) {
    out.push({
      key: 'expiry_hours',
      state: 'warn',
      word: 'Not entered',
      message: 'Enter the expiry hours from the page.',
    });
  } else if (!a) {
    out.push({
      key: 'expiry_hours',
      state: 'info',
      word: 'No aircraft',
      message: 'Choose the aircraft to check the hours left.',
    });
  } else if (status!.ttis_now_h === null) {
    out.push({
      key: 'expiry_hours',
      state: 'info',
      word: 'TTIS unknown',
      message: `Set ${a.registration}'s TTIS at start in Project settings to check the hours left.`,
    });
  } else {
    const now = tenth(status!.ttis_now_h);
    const left = tenth(v.expiry_hours - now);
    const sum = `${h1(v.expiry_hours)} − ${h1(now)}`;
    out.push(
      left < 0
        ? {
            key: 'expiry_hours',
            state: 'fail',
            word: 'Hours expired',
            message: `MR hours ran out: ${a.registration} is ${h1(-left)} h past them (${sum}).`,
          }
        : warnH > 0 && left <= warnH
          ? { key: 'expiry_hours', state: 'warn', word: 'Hours low', message: `${h1(left)} h left: ${sum}.` }
          : {
              key: 'expiry_hours',
              state: 'pass',
              word: 'Hours left',
              message: `${h1(left)} h left: ${sum}.`,
            },
    );
  }
  if (a) {
    if (v.registration !== null && v.registration !== a.registration.toUpperCase()) {
      out.push({
        key: 'registration',
        state: 'fail',
        word: 'Other aircraft',
        message: `The page is for ${v.registration}, not ${a.registration}.`,
      });
    }
    const issue = status!.issue_ttis_h;
    if (v.issue_hours !== null) {
      if (issue === null) {
        out.push({
          key: 'issue_hours',
          state: 'info',
          word: 'Not in register',
          message: `${a.registration} has no TTIS at MR issue in Project settings; the page says ${h1(v.issue_hours)} h.`,
        });
      } else if (tenth(issue) !== tenth(v.issue_hours)) {
        out.push({
          key: 'issue_hours',
          state: 'warn',
          word: 'Differs',
          message: `The page was issued at ${h1(v.issue_hours)} h; Teslyn has ${h1(issue)} h for ${a.registration}'s current MR. Use the page for the aircraft if it is the current MR.`,
        });
      } else {
        out.push({
          key: 'issue_hours',
          state: 'pass',
          word: 'Matches',
          message: `Issued at ${h1(issue)} h, as Teslyn has.`,
        });
      }
    }
    if (v.expiry_hours !== null && issue !== null && a.mr_interval_h !== null) {
      const expected = tenth(issue + a.mr_interval_h);
      out.push(
        expected === tenth(v.expiry_hours)
          ? {
              key: 'interval',
              state: 'pass',
              word: 'Matches',
              message: `Expiry hours ${h1(v.expiry_hours)} = ${h1(issue)} + ${h1(a.mr_interval_h)} h MR hours.`,
            }
          : {
              key: 'interval',
              state: 'warn',
              word: 'Differs',
              message: `The page expires at ${h1(v.expiry_hours)} h; Teslyn's MR issue and MR hours give ${h1(expected)} h (${h1(issue)} + ${h1(a.mr_interval_h)}).`,
            },
      );
    }
  }
  return out;
}

/**
 * What "Use for the aircraft" changes in the aircraft register from a front page: TTIS at MR issue and MR hours
 * (expiry hours − issue hours). Null when the page lacks either figure or the register already agrees.
 */
export function mrRegisterUpdate(
  v: MrFrontValues,
  a: Pick<Aircraft, 'ttis_at_issue_h' | 'mr_interval_h'>,
): { readonly ttis_at_issue_h: number; readonly mr_interval_h: number } | null {
  if (v.issue_hours === null || v.expiry_hours === null || v.expiry_hours <= v.issue_hours) return null;
  const next = {
    ttis_at_issue_h: tenth(v.issue_hours),
    mr_interval_h: tenth(v.expiry_hours - v.issue_hours),
  };
  const same =
    a.ttis_at_issue_h !== null &&
    a.mr_interval_h !== null &&
    tenth(a.ttis_at_issue_h) === next.ttis_at_issue_h &&
    tenth(a.mr_interval_h) === next.mr_interval_h;
  return same ? null : next;
}

// ------------------------------------------------------------------------------------------------ MR daily log page

export interface MrLogRowCheck {
  readonly index: number;
  readonly date: string | null;
  /** The day's flights in Teslyn, when the row has a date and the aircraft flew. */
  readonly day: DayFlights | null;
  readonly checks: readonly DocumentCheck[];
  readonly state: DocumentCheckState;
}

export interface MrLogResult {
  readonly rows: readonly MrLogRowCheck[];
  /** Checks of the page as a whole (aircraft, days missing from the page). */
  readonly checks: readonly DocumentCheck[];
  readonly state: DocumentCheckState;
}

/**
 * The MR daily log page's checks (decision 507): each row's flight time against the day's flight entries of the
 * aircraft, its progressive total against the row before and Teslyn's progressive TTIS, and the days flown between
 * the first and last row that have no row.
 */
export function mrLogChecks(
  v: MrLogValues,
  aircraft: Aircraft | null,
  entries: readonly DailyEntry[],
): MrLogResult {
  const page: DocumentCheck[] = [];
  if (!aircraft) {
    page.push({
      key: 'aircraft',
      state: 'info',
      word: 'No aircraft',
      message: 'Choose the aircraft to check the page against its flights.',
    });
  } else if (v.registration !== null && v.registration !== aircraft.registration.toUpperCase()) {
    page.push({
      key: 'registration',
      state: 'fail',
      word: 'Other aircraft',
      message: `The page is for ${v.registration}, not ${aircraft.registration}.`,
    });
  }
  const days = aircraft ? flightsByDay(aircraft, entries) : [];
  const byDate = new Map(days.map((d) => [d.date, d]));
  const firstEntry = aircraft
    ? (chronological(entries.filter((e) => e.deleted_at === null && e.aircraft_id === aircraft.id))[0]
        ?.date ?? null)
    : null;
  let previous = v.brought_forward_hours;
  const rows: MrLogRowCheck[] = v.rows.map((row, index) => {
    const checks: DocumentCheck[] = [];
    const day = row.date ? (byDate.get(row.date) ?? null) : null;
    const on = row.date ? dayText(row.date, false) : `row ${index + 1}`;
    if (row.date === null) {
      checks.push({
        key: 'date',
        state: 'warn',
        word: 'No date',
        message: `Enter the date of row ${index + 1}.`,
      });
    } else if (aircraft) {
      if (firstEntry === null || row.date < firstEntry) {
        checks.push({
          key: 'flights',
          state: 'info',
          word: 'Before this project',
          message: `${on} is before ${aircraft.registration}'s first entry on this project: not checked.`,
        });
      } else if (row.flight_hours === null) {
        checks.push({
          key: 'flights',
          state: 'warn',
          word: 'Not entered',
          message: `Enter the flight time of ${on}.`,
        });
      } else if (!day) {
        checks.push({
          key: 'flights',
          state: 'fail',
          word: 'No flights',
          message: `The MR says ${h1(row.flight_hours)} h on ${on}; no flight of ${aircraft.registration} is recorded in Teslyn that day.`,
        });
      } else if (tenth(row.flight_hours) === day.total_h) {
        checks.push({
          key: 'flights',
          state: 'pass',
          word: 'Matches',
          message: `${h1(row.flight_hours)} h = ${sumText(day)}.`,
        });
      } else {
        checks.push({
          key: 'flights',
          state: 'fail',
          word: 'Does not match',
          message: `The MR says ${h1(row.flight_hours)} h on ${on}; Teslyn's flights ${h1(day.total_h)} h (${sumText(day)}).`,
        });
      }
    }
    if (row.progressive_hours !== null && previous !== null && row.flight_hours !== null) {
      const sum = tenth(previous + row.flight_hours);
      if (sum !== tenth(row.progressive_hours))
        checks.push({
          key: 'running_total',
          state: 'warn',
          word: 'Does not add up',
          message: `${h1(previous)} + ${h1(row.flight_hours)} = ${h1(sum)}; the page says ${h1(row.progressive_hours)}.`,
        });
    }
    if (
      day &&
      day.ttis_after_h !== null &&
      row.progressive_hours !== null &&
      tenth(row.progressive_hours) !== day.ttis_after_h
    ) {
      checks.push({
        key: 'ttis',
        state: 'warn',
        word: 'TTIS differs',
        message: `The page's progressive total ${h1(row.progressive_hours)} h; Teslyn's TTIS after ${on} ${h1(day.ttis_after_h)} h.`,
      });
    }
    if (row.progressive_hours !== null) previous = row.progressive_hours;
    else if (previous !== null && row.flight_hours !== null) previous += row.flight_hours;
    return { index, date: row.date, day, checks, state: worstState(checks) };
  });
  const dated = v.rows
    .map((r) => r.date)
    .filter((d): d is string => d !== null)
    .sort();
  if (aircraft && dated.length > 0) {
    const first = dated[0]!;
    const last = dated[dated.length - 1]!;
    const onPage = new Set(dated);
    for (const d of days) {
      if (d.date >= first && d.date <= last && !onPage.has(d.date))
        page.push({
          key: `missing_${d.date}`,
          state: 'fail',
          word: 'Not on the page',
          message: `${aircraft.registration} flew on ${dayText(d.date, false)} (${h1(d.total_h)} h: ${sumText(d)}) but the day is not on this page.`,
        });
    }
  }
  if (v.rows.length === 0)
    page.push({ key: 'rows', state: 'warn', word: 'No rows', message: 'Enter the page’s daily rows.' });
  return { rows, checks: page, state: worstState([...page, ...rows]) };
}

// ------------------------------------------------------------------------------------------------ fuel

/** Litres of a docket's quantity (US gallons converted), or null. */
export function docketLitres(v: Pick<DocketValues, 'litres' | 'unit'>): number | null {
  if (v.litres === null) return null;
  return v.unit === 'USG' ? Math.round(v.litres * LITRES_PER_USG * 10) / 10 : v.litres;
}

const FUEL_WORD = { avgas: 'AVGAS', turbine: 'AVTUR' } as const;

function litresCheck(what: string, docket: number, recorded: number, recordText: string): DocumentCheck {
  return Math.abs(docket - recorded) <= LITRES_TOLERANCE
    ? {
        key: 'litres',
        state: 'pass',
        word: 'Matches',
        message: `${litresText(docket)} L, the same as ${recordText}.`,
      }
    : {
        key: 'litres',
        state: 'fail',
        word: 'Does not match',
        message: `The ${what} says ${litresText(docket)} L; ${recordText} is ${litresText(recorded)} L.`,
      };
}

function dateCheck(what: string, docketDate: string | null, recordDate: string): DocumentCheck {
  if (docketDate === null)
    return { key: 'date', state: 'warn', word: 'Not entered', message: `Enter the ${what}'s date.` };
  return docketDate === recordDate
    ? { key: 'date', state: 'pass', word: 'Same day', message: `Dated ${dayText(recordDate)}.` }
    : {
        key: 'date',
        state: 'warn',
        word: 'Other day',
        message: `The ${what} is dated ${dayText(docketDate)}; the record is ${dayText(recordDate)}.`,
      };
}

/**
 * A delivery docket against its resupply (decisions 159, 193): the date, the fuel type delivered to the store, and
 * the litres against the amount uplifted of that fuel type (drums × litres per drum).
 */
export function fuelDocketChecks(
  v: DocketValues,
  resupply: FuelLedgerEntry | null,
  drumL: number,
): DocumentCheck[] {
  if (!resupply)
    return [
      {
        key: 'record',
        state: 'info',
        word: 'No resupply',
        message: 'Choose the resupply this docket is for.',
      },
    ];
  const out: DocumentCheck[] = [dateCheck('docket', v.date, resupply.date)];
  const litres = docketLitres(v);
  const types = (['avgas', 'turbine'] as const).filter(
    (f) =>
      (f === 'avgas'
        ? resupply.truck_avgas_l + resupply.drums_avgas
        : resupply.truck_turbine_l + resupply.drums_turbine) > 0,
  );
  const fuel = v.fuel_type ?? (types.length === 1 ? types[0]! : null);
  if (v.fuel_type !== null && types.length > 0 && !types.includes(v.fuel_type))
    out.push({
      key: 'fuel_type',
      state: 'fail',
      word: 'Other fuel',
      message: `The docket is for ${FUEL_WORD[v.fuel_type]}; the resupply records ${types.map((t) => FUEL_WORD[t]).join(' and ')}.`,
    });
  if (litres === null) {
    out.push({
      key: 'litres',
      state: 'warn',
      word: 'Not entered',
      message: 'Enter the quantity on the docket.',
    });
    return out;
  }
  if (fuel === null) {
    out.push({
      key: 'litres',
      state: 'warn',
      word: 'Fuel type',
      message: 'Enter the fuel type on the docket to check the quantity.',
    });
    return out;
  }
  const truck = fuel === 'avgas' ? resupply.uplift_truck_avgas_l : resupply.uplift_truck_turbine_l;
  const drums = fuel === 'avgas' ? resupply.uplift_drums_avgas : resupply.uplift_drums_turbine;
  if ((truck ?? null) === null && (drums ?? null) === null) {
    out.push({
      key: 'litres',
      state: 'warn',
      word: 'No uplift',
      message: `The resupply records no amount uplifted: enter ${litresText(litres)} L of ${FUEL_WORD[fuel]} from the docket in the resupply.`,
    });
    return out;
  }
  const recorded = (truck ?? 0) + (drums ?? 0) * drumL;
  out.push(litresCheck('docket', litres, recorded, `the resupply's ${FUEL_WORD[fuel]} uplifted`));
  return out;
}

/** A fuel receipt against its refuel: the date, the aircraft, its fuel type and the refuel litres. */
export function fuelReceiptChecks(
  v: DocketValues,
  entry: DailyEntry | null,
  aircraft: readonly Aircraft[],
): DocumentCheck[] {
  if (!entry)
    return [
      {
        key: 'record',
        state: 'info',
        word: 'No refuel',
        message: 'Choose the daily entry this receipt is for.',
      },
    ];
  const out: DocumentCheck[] = [dateCheck('receipt', v.date, entry.date)];
  const a = aircraft.find((x) => x.id === entry.aircraft_id) ?? null;
  if (v.registration !== null && a && v.registration !== a.registration.toUpperCase())
    out.push({
      key: 'registration',
      state: 'fail',
      word: 'Other aircraft',
      message: `The receipt is for ${v.registration}; the entry is ${a.registration}'s.`,
    });
  const fuel = a ? aircraftFuelType(a) : null;
  if (v.fuel_type !== null && fuel !== null && v.fuel_type !== fuel)
    out.push({
      key: 'fuel_type',
      state: 'fail',
      word: 'Other fuel',
      message: `The receipt is for ${FUEL_WORD[v.fuel_type]}; ${a!.registration} burns ${FUEL_WORD[fuel]}.`,
    });
  const litres = docketLitres(v);
  if (litres === null)
    out.push({
      key: 'litres',
      state: 'warn',
      word: 'Not entered',
      message: 'Enter the quantity on the receipt.',
    });
  else out.push(litresCheck('receipt', litres, entry.fuel_load_l, "the entry's refuel"));
  return out;
}

// ------------------------------------------------------------------------------------------------ one document

export interface DocumentCheckContext {
  readonly aircraft: readonly Aircraft[];
  readonly entries: readonly DailyEntry[];
  readonly fuel: readonly FuelLedgerEntry[];
  readonly today: string;
  readonly warnH?: number;
  readonly drumL: number;
}

/** The fields of a `document_scan` the checks read. */
export interface DocumentScanLike {
  readonly kind: DocumentKind;
  readonly aircraft_id: Uuid | null;
  readonly fuel_entry_id: Uuid | null;
  readonly daily_entry_id: Uuid | null;
}

export interface DocumentCheckResult {
  readonly checks: readonly DocumentCheck[];
  /** MR log pages only. */
  readonly log?: MrLogResult;
  readonly state: DocumentCheckState;
  /** One line for a list: "MR front page: Valid to 25 Sep 2027; 83.9 h left". */
  readonly headline: string;
}

/** Every check of one document, from its checked values. */
export function documentChecks(
  scan: DocumentScanLike,
  values: DocumentValues,
  ctx: DocumentCheckContext,
): DocumentCheckResult {
  const plane = ctx.aircraft.find((a) => a.id === scan.aircraft_id) ?? null;
  let checks: DocumentCheck[];
  let log: MrLogResult | undefined;
  if (scan.kind === 'mr_front') {
    checks = mrFrontChecks(values as MrFrontValues, {
      aircraft: plane,
      entries: ctx.entries,
      today: ctx.today,
      ...(ctx.warnH !== undefined ? { warnH: ctx.warnH } : {}),
    });
  } else if (scan.kind === 'mr_log') {
    log = mrLogChecks(values as MrLogValues, plane, ctx.entries);
    checks = [
      ...log.checks,
      ...log.rows.flatMap((r) => r.checks.filter((c) => c.state !== 'pass' && c.state !== 'info')),
    ];
  } else if (scan.kind === 'fuel_docket') {
    checks = fuelDocketChecks(
      values as DocketValues,
      ctx.fuel.find((f) => f.id === scan.fuel_entry_id && f.deleted_at === null) ?? null,
      ctx.drumL,
    );
  } else {
    checks = fuelReceiptChecks(
      values as DocketValues,
      ctx.entries.find((e) => e.id === scan.daily_entry_id && e.deleted_at === null) ?? null,
      ctx.aircraft,
    );
  }
  const state = log ? worstState([...checks, { state: log.state }]) : worstState(checks);
  let headline: string;
  if (log) {
    const bad =
      log.rows.filter((r) => r.state === 'fail').length + log.checks.filter((c) => c.state === 'fail').length;
    const good = log.rows.filter((r) =>
      r.checks.some((c) => c.key === 'flights' && c.state === 'pass'),
    ).length;
    headline =
      bad > 0
        ? `${bad} difference${bad === 1 ? '' : 's'} with Teslyn's flights; ${good} day${good === 1 ? '' : 's'} match`
        : `${good} day${good === 1 ? '' : 's'} match Teslyn's flights`;
  } else {
    const shown = checks.filter((c) => c.state === state);
    headline = (shown.length > 0 ? shown : checks)
      .map((c) => c.message.replace(/\.$/, ''))
      .slice(0, 2)
      .join('; ');
  }
  return {
    checks,
    ...(log ? { log } : {}),
    state,
    headline: `${DOCUMENT_KIND_LABEL[scan.kind]}: ${headline}`,
  };
}
